import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  GraduationCap,
  Loader2,
  LogIn,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import {
  useTrainingSession,
  TRAINING_ACTIVITIES,
  TRAINING_ACTIVITY_LABELS,
  TRAINING_ACTIVITY_PATHS,
  cleanServiceNumber,
  formatServiceNumber,
  isValidServiceNumber,
} from "../../context/TrainingSessionContext";

/*
 * ---------------------------------------------------------------------------
 * 1471 HORWICH SQUADRON
 * CADET TRAINING HOME
 * ---------------------------------------------------------------------------
 *
 * No cadet name is required.
 *
 * The service number identifies the anonymous training record.
 *
 * Example:
 *
 * 30 1471 0026 001
 *
 * Any authorised cadet tablet can open the same training record.
 */

const ACTIVITY_CARDS = [
  {
    key: TRAINING_ACTIVITIES.NEW_STARTER,
    title: "New Starter",
    subtitle: "Introduction & Induction",
    description:
      "Start your journey at 1471 Squadron and work through your initial training.",
    icon: Sparkles,
  },
  {
    key: TRAINING_ACTIVITIES.FIRST_CLASS,
    title: "First Class",
    subtitle: "First Class Cadet Training",
    description:
      "Continue your core Air Cadet training towards First Class classification.",
    icon: BookOpen,
  },
  {
    key: TRAINING_ACTIVITIES.LEADING,
    title: "Leading",
    subtitle: "Leading Cadet Training",
    description:
      "Continue your classification training towards Leading Cadet.",
    icon: GraduationCap,
  },
];

function TrainingHome() {
  const navigate = useNavigate();

  const inputRef = useRef(null);

  const {
    active,
    serviceNumber: activeServiceNumber,
    activity: activeActivity,

    loading,
    starting,
    error,
    setError,

    bookmarkCode,
    dismissBookmark,

    startSession,

    activities,
  } = useTrainingSession();

  const [serviceNumberInput, setServiceNumberInput] =
    useState("");

  const [selectedActivity, setSelectedActivity] =
    useState("");

  const [localError, setLocalError] =
    useState("");

  /*
   * -----------------------------------------------------------------------
   * If this tablet already has an active session
   * -----------------------------------------------------------------------
   *
   * This can happen after:
   *
   * - browser refresh
   * - browser restart
   * - accidental navigation back to /training
   *
   * Take the cadet back into the activity that was already open.
   */
  useEffect(() => {
    if (
      loading ||
      !active ||
      !activeServiceNumber ||
      !activeActivity
    ) {
      return;
    }

    const path =
      TRAINING_ACTIVITY_PATHS[
        activeActivity
      ];

    if (path) {
      navigate(path, {
        replace: true,
      });
    }
  }, [
    active,
    activeActivity,
    activeServiceNumber,
    loading,
    navigate,
  ]);

  /*
   * Focus the service-number box when the screen opens.
   */
  useEffect(() => {
    if (
      !loading &&
      !bookmarkCode
    ) {
      window.setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [
    bookmarkCode,
    loading,
  ]);

  /*
   * -----------------------------------------------------------------------
   * Service number
   * -----------------------------------------------------------------------
   */

  const cleanNumber = useMemo(
    () =>
      cleanServiceNumber(
        serviceNumberInput
      ),
    [serviceNumberInput]
  );

  const validServiceNumber =
    isValidServiceNumber(
      cleanNumber
    );

  const displayNumber =
    formatServiceNumber(
      cleanNumber
    );

  const handleServiceNumberChange = (
    event
  ) => {
    const cleaned =
      cleanServiceNumber(
        event.target.value
      );

    setServiceNumberInput(cleaned);

    setLocalError("");
    setError("");
  };

  /*
   * -----------------------------------------------------------------------
   * Start activity
   * -----------------------------------------------------------------------
   */

  const openActivity = async (
    activityKey
  ) => {
    setLocalError("");
    setError("");

    if (!validServiceNumber) {
      setLocalError(
        "Enter your 13-digit 1471 service number first."
      );

      inputRef.current?.focus();

      return;
    }

    setSelectedActivity(
      activityKey
    );

    try {
      await startSession(
        cleanNumber,
        activityKey
      );

      const path =
        TRAINING_ACTIVITY_PATHS[
          activityKey
        ];

      navigate(path);
    } catch (err) {
      setLocalError(
        err?.message ||
          "Unable to open this training record."
      );
    }
  };

  /*
   * -----------------------------------------------------------------------
   * Pressing Enter after typing the service number
   * -----------------------------------------------------------------------
   *
   * We deliberately do NOT automatically choose an activity.
   *
   * Enter simply moves the cadet's attention to the three choices.
   */
  const handleKeyDown = (
    event
  ) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    if (!validServiceNumber) {
      setLocalError(
        "Check your service number and try again."
      );

      return;
    }

    document
      .getElementById(
        "training-activities"
      )
      ?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
  };

  /*
   * -----------------------------------------------------------------------
   * Loading
   * -----------------------------------------------------------------------
   */

  if (loading) {
    return (
      <div className="min-h-screen bg-raf-navy flex flex-col items-center justify-center text-white">
        <Loader2 className="w-10 h-10 animate-spin" />

        <p className="mt-4 text-white/70">
          Preparing cadet training...
        </p>
      </div>
    );
  }

  /*
   * -----------------------------------------------------------------------
   * End-session confirmation
   * -----------------------------------------------------------------------
   *
   * When a cadet finishes a session the shared context retains their
   * service number long enough to show it here.
   *
   * Pressing "Tablet ready" clears the confirmation and prepares the
   * device for the next cadet.
   */

  if (bookmarkCode) {
    return (
      <div className="min-h-screen bg-raf-navy flex items-center justify-center p-5">
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-green-700 text-white p-8 text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4" />

              <h1 className="text-3xl font-bold">
                Session Saved
              </h1>

              <p className="mt-2 text-white/80">
                Your training progress has
                been saved.
              </p>
            </div>

            <div className="p-8 text-center">
              <p className="text-slate-500 text-sm uppercase tracking-widest font-semibold">
                Your service number
              </p>

              <div className="mt-4 rounded-2xl bg-slate-100 border-2 border-slate-200 px-4 py-6">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black tracking-wider text-raf-navy">
                  {formatServiceNumber(
                    bookmarkCode
                  )}
                </div>
              </div>

              <p className="mt-5 text-slate-600 max-w-lg mx-auto">
                Use this number on any
                authorised 1471 cadet
                training tablet to continue
                where you left off.
              </p>

              <div className="mt-7 rounded-xl bg-amber-50 border border-amber-200 p-4 text-left">
                <div className="flex gap-3">
                  <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />

                  <p className="text-sm text-amber-900">
                    Remember your service
                    number. Do not use
                    somebody else's training
                    record.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  dismissBookmark();
                  setServiceNumberInput("");
                  setSelectedActivity("");
                  setLocalError("");

                  window.setTimeout(
                    () =>
                      inputRef.current?.focus(),
                    100
                  );
                }}
                className="mt-8 w-full rounded-xl bg-raf-navy text-white font-bold text-lg py-4 px-6 flex items-center justify-center gap-3 hover:opacity-95 active:scale-[0.99] transition"
              >
                <RotateCcw className="w-5 h-5" />

                Tablet Ready for Next Cadet
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /*
   * -----------------------------------------------------------------------
   * Main training home
   * -----------------------------------------------------------------------
   */

  const visibleError =
    localError || error;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* ================================================================
          HEADER
      ================================================================= */}

      <header className="bg-raf-navy text-white border-b-4 border-raf-red">
        <div className="max-w-6xl mx-auto px-5 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
              <GraduationCap className="w-8 h-8" />
            </div>

            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">
                1471 Horwich Squadron
              </p>

              <h1 className="text-2xl sm:text-3xl font-bold">
                Cadet Training
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* ================================================================
          PAGE
      ================================================================= */}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* --------------------------------------------------------------
            INTRO
        --------------------------------------------------------------- */}

        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-raf-navy">
            What are you working on today?
          </h2>

          <p className="mt-3 text-slate-600 text-base sm:text-lg">
            Enter your service number and
            choose your training activity.
          </p>
        </div>

        {/* --------------------------------------------------------------
            SERVICE NUMBER
        --------------------------------------------------------------- */}

        <section className="mt-8 max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 sm:p-7">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-12 h-12 rounded-xl bg-slate-100 items-center justify-center shrink-0">
                  <UserRound className="w-6 h-6 text-raf-navy" />
                </div>

                <div className="flex-1">
                  <label
                    htmlFor="service-number"
                    className="block text-lg font-bold text-raf-navy"
                  >
                    Your service number
                  </label>

                  <p className="text-sm text-slate-500 mt-1">
                    Example:
                    {" "}
                    30 1471 0026 001
                  </p>

                  <div className="mt-4 relative">
                    <input
                      ref={inputRef}
                      id="service-number"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={16}
                      value={
                        displayNumber
                      }
                      onChange={
                        handleServiceNumberChange
                      }
                      onKeyDown={
                        handleKeyDown
                      }
                      placeholder="30 1471 0026 001"
                      className={[
                        "w-full rounded-xl border-2 px-4 sm:px-5 py-4 text-xl sm:text-2xl font-bold tracking-wider outline-none transition",
                        cleanNumber.length ===
                          0
                          ? "border-slate-300 focus:border-raf-navy"
                          : validServiceNumber
                          ? "border-green-500 focus:border-green-600 bg-green-50/40"
                          : "border-amber-400 focus:border-amber-500",
                      ].join(" ")}
                    />

                    {validServiceNumber && (
                      <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-green-600" />
                    )}
                  </div>

                  {cleanNumber.length >
                    0 &&
                    !validServiceNumber && (
                      <p className="mt-2 text-sm text-amber-700">
                        A 1471 service
                        number contains 13
                        digits.
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --------------------------------------------------------------
            ERROR
        --------------------------------------------------------------- */}

        {visibleError && (
          <div className="mt-5 max-w-3xl mx-auto">
            <div className="rounded-xl bg-red-50 border border-red-200 p-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />

                <div>
                  <p className="font-semibold text-red-900">
                    Unable to continue
                  </p>

                  <p className="text-sm text-red-800 mt-1">
                    {visibleError}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --------------------------------------------------------------
            ACTIVITIES
        --------------------------------------------------------------- */}

        <section
          id="training-activities"
          className="mt-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ACTIVITY_CARDS.map(
              (card) => {
                const Icon =
                  card.icon;

                const progress =
                  activities?.[
                    card.key
                  ];

                const complete =
                  Boolean(
                    progress?.completed
                  );

                const hasProgress =
                  Boolean(
                    progress?.current_step
                  ) ||
                  Number(
                    progress?.module_count ||
                      0
                  ) > 0;

                const busy =
                  starting &&
                  selectedActivity ===
                    card.key;

                return (
                  <button
                    key={card.key}
                    type="button"
                    disabled={
                      starting
                    }
                    onClick={() =>
                      openActivity(
                        card.key
                      )
                    }
                    className={[
                      "group relative text-left bg-white rounded-2xl border-2 p-6 min-h-[275px]",
                      "transition-all duration-200",
                      "hover:-translate-y-1 hover:shadow-lg",
                      "active:translate-y-0",
                      "disabled:opacity-60 disabled:cursor-wait",
                      complete
                        ? "border-green-400"
                        : "border-slate-200 hover:border-raf-navy",
                    ].join(" ")}
                  >
                    {complete && (
                      <div className="absolute top-4 right-4">
                        <div className="flex items-center gap-1.5 bg-green-100 text-green-800 rounded-full px-3 py-1 text-xs font-bold">
                          <CheckCircle2 className="w-4 h-4" />

                          Complete
                        </div>
                      </div>
                    )}

                    <div className="w-14 h-14 rounded-2xl bg-slate-100 text-raf-navy flex items-center justify-center group-hover:bg-raf-navy group-hover:text-white transition-colors">
                      <Icon className="w-7 h-7" />
                    </div>

                    <p className="mt-5 text-xs uppercase tracking-widest text-slate-500 font-bold">
                      {card.subtitle}
                    </p>

                    <h3 className="mt-1 text-2xl font-black text-raf-navy">
                      {card.title}
                    </h3>

                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {card.description}
                    </p>

                    <div className="mt-6 flex items-center justify-between">
                      <span className="text-sm font-bold text-raf-navy">
                        {complete
                          ? "Review training"
                          : hasProgress
                          ? "Continue"
                          : "Start"}
                      </span>

                      {busy ? (
                        <Loader2 className="w-5 h-5 text-raf-navy animate-spin" />
                      ) : (
                        <ArrowRight className="w-5 h-5 text-raf-navy transition-transform group-hover:translate-x-1" />
                      )}
                    </div>
                  </button>
                );
              }
            )}
          </div>
        </section>

        {/* --------------------------------------------------------------
            INFORMATION
        --------------------------------------------------------------- */}

        <section className="mt-8 max-w-4xl mx-auto">
          <div className="rounded-2xl bg-blue-50 border border-blue-200 p-5">
            <div className="flex items-start gap-3">
              <LogIn className="w-5 h-5 text-raf-navy shrink-0 mt-0.5" />

              <div>
                <h3 className="font-bold text-raf-navy">
                  Using another tablet?
                </h3>

                <p className="mt-1 text-sm text-slate-700">
                  That's fine. Your
                  training progress follows
                  your service number, not
                  the tablet. Enter the
                  same number on any
                  authorised 1471 cadet
                  training tablet.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* --------------------------------------------------------------
            PRIVACY
        --------------------------------------------------------------- */}

        <footer className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4" />

            No cadet name, email address
            or password is required.
          </div>
        </footer>
      </main>
    </div>
  );
}

export default TrainingHome;